import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import adminProviderNotificationRostersService from "../../services/adminProviderNotificationRostersService";
import type { ProviderNotificationRoster, ProviderNotificationUser } from "../../types/providerNotificationRosters";
import { formatNumber } from "../../utils/numberUtils";

const notificationChannels = [
  { field: "shouldEmail", label: "Email" },
  { field: "shouldWebInbox", label: "Web" },
  { field: "shouldMobileInbox", label: "Mobile in-app" },
  { field: "shouldPush", label: "Mobile push" },
] as const;

type NotificationChannelField = typeof notificationChannels[number]["field"];
type ChannelFilter = "all" | "on" | "off";
type ChannelFilters = Record<NotificationChannelField, ChannelFilter>;
const defaultChannelFilters: ChannelFilters = {
  shouldEmail: "all", shouldWebInbox: "all", shouldMobileInbox: "all", shouldPush: "all",
};

function NotificationUsersTable({ users }: { users: ProviderNotificationUser[] }) {
  const [userSortDirection, setUserSortDirection] = useState<"ascending" | "descending">("ascending");
  const [channelFilters, setChannelFilters] = useState<ChannelFilters>(defaultChannelFilters);
  const hasActiveFilters = notificationChannels.some(channel => channelFilters[channel.field] !== "all");
  const visibleUsers = users.filter(notificationUser => notificationChannels.every(channel => {
    const channelFilter = channelFilters[channel.field];
    return channelFilter === "all" || notificationUser[channel.field] === (channelFilter === "on");
  })).sort((firstUser, secondUser) => {
    const nameComparison = (firstUser.name || firstUser.email).localeCompare(secondUser.name || secondUser.email, undefined, { sensitivity: "base" });
    const userComparison = nameComparison || firstUser.email.localeCompare(secondUser.email) || firstUser.id.localeCompare(secondUser.id);
    return userSortDirection === "ascending" ? userComparison : -userComparison;
  });

  if (users.length === 0) {
    return <p className="py-24 text-center text-sm text-gray-600 dark:text-gray-300">No users have notifications turned on for this provider.</p>;
  }
  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-rp-separator">
          <caption className="sr-only">Users with saved notification choices for the selected provider</caption>
          <thead className="bg-gray-50 dark:bg-rp-surface-sunken">
            <tr>
              <th scope="col" aria-sort={userSortDirection} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <button type="button" onClick={() => setUserSortDirection(currentDirection => currentDirection === "ascending" ? "descending" : "ascending")}
                  aria-label={`Sort users ${userSortDirection === "ascending" ? "Z to A" : "A to Z"}`}
                  className="inline-flex items-center gap-1 rounded uppercase focus:outline-none focus:ring-2 focus:ring-rp-amber hover:text-rp-amber">
                  User
                  {userSortDirection === "ascending" ? <ArrowUp aria-hidden="true" className="h-3 w-3" /> : <ArrowDown aria-hidden="true" className="h-3 w-3" />}
                </button>
              </th>
              {notificationChannels.map(channel => (
                <th key={channel.field} scope="col" className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <label className="flex flex-col items-start gap-2">
                    {channel.label}
                    <select value={channelFilters[channel.field]}
                      onChange={event => {
                        const selectedFilter = event.target.value as ChannelFilter;
                        setChannelFilters(currentFilters => ({ ...currentFilters, [channel.field]: selectedFilter }));
                      }}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal text-gray-900 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface-raised dark:text-white">
                      <option value="all">All</option>
                      <option value="on">On</option>
                      <option value="off">Off</option>
                    </select>
                  </label>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-rp-separator dark:bg-black">
            {visibleUsers.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-600 dark:text-gray-300">No users match the selected channel filters.</td></tr>}
            {visibleUsers.map(notificationUser => (
              <tr key={notificationUser.id} className="transition-colors hover:bg-gray-50 dark:hover:bg-rp-surface">
                <th scope="row" className="px-4 py-3 text-left font-normal">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">{notificationUser.name || notificationUser.email}</div>
                  {notificationUser.name && <div className="text-xs text-gray-500 dark:text-gray-400">{notificationUser.email}</div>}
                </th>
                {notificationChannels.map(channel => (
                  <td key={channel.field} className="px-4 py-3 text-sm">
                    <span className={notificationUser[channel.field]
                      ? "font-semibold text-green-600 dark:text-green-500"
                      : "text-gray-500 dark:text-gray-400"}>{notificationUser[channel.field] ? "On" : "Off"}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 p-4 dark:border-rp-separator">
        <p aria-live="polite" className="text-sm text-gray-600 dark:text-gray-300">Showing {formatNumber(visibleUsers.length, 0)} of {formatNumber(users.length, 0)} users. Users must match every selected filter.</p>
        <button type="button" onClick={() => setChannelFilters(defaultChannelFilters)} disabled={!hasActiveFilters}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600">Clear filters</button>
      </div>
    </>
  );
}

interface ProviderNotificationsTabProps {
  providers: { id: number; name: string }[];
  providersLoading: boolean;
  providersError: string | null;
  onRetryProviders: () => void;
}

interface ProviderRosterState {
  providerId: number;
  roster: ProviderNotificationRoster | null;
  loading: boolean;
  error: string | null;
}

export default function ProviderNotificationsTab({ providers, providersLoading, providersError, onRetryProviders }: ProviderNotificationsTabProps) {
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [rosterState, setRosterState] = useState<ProviderRosterState | null>(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const rosterRequestIdRef = useRef(0);
  const selectedProvider = providers.find(provider => provider.id === selectedProviderId) ?? providers[0];
  const providerId = selectedProvider?.id;

  useEffect(() => {
    const requestId = ++rosterRequestIdRef.current;
    if (providerId === undefined) return;
    const controller = new AbortController();
    const isCurrentRequest = () => requestId === rosterRequestIdRef.current && !controller.signal.aborted;
    setRosterState({ providerId, roster: null, loading: true, error: null });
    adminProviderNotificationRostersService.getProviderRoster(providerId, controller.signal)
      .then(roster => {
        if (!isCurrentRequest()) return;
        setRosterState({ providerId, roster, loading: false, error: null });
      })
      .catch(() => {
        if (!isCurrentRequest()) return;
        setRosterState({ providerId, roster: null, loading: false, error: "Unable to load provider notification users. Please try again." });
      });
    return () => controller.abort();
  }, [providerId, refreshVersion]);

  const currentRosterState = rosterState?.providerId === providerId ? rosterState : null;
  const loading = providersLoading || (providerId !== undefined && (!currentRosterState || currentRosterState.loading));
  const loadError = providersError || currentRosterState?.error;
  const rosterUsers = currentRosterState?.roster?.users;

  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 bg-white transition-colors duration-300 dark:border-rp-separator dark:bg-rp-surface">
      <div className="space-y-4 border-b border-gray-200 p-4 dark:border-rp-separator">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Provider notifications</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Each user appears once per provider. On means enabled for at least one portfolio.</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Saved choices only. Delivery also depends on portfolio access and available email or push destinations.</p>
          </div>
          <button type="button" onClick={() => providersError ? onRetryProviders() : setRefreshVersion(currentVersion => currentVersion + 1)} disabled={loading || (!providersError && providerId === undefined)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600">
            {loadError ? "Try again" : "Refresh"}
          </button>
        </div>
        {!providersLoading && !providersError && selectedProvider && (
          <div className="flex flex-wrap items-center gap-3">
            <label htmlFor="notification-provider" className="text-sm font-semibold text-gray-900 dark:text-white">Provider</label>
            <select id="notification-provider" value={selectedProvider.id} onChange={event => setSelectedProviderId(Number(event.target.value))}
              className="max-w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface-raised dark:text-white">
              {providers.map(provider => <option key={provider.id} value={provider.id}>{provider.name}</option>)}
            </select>
            {!loading && !loadError && rosterUsers && <span className="text-sm text-gray-600 dark:text-gray-300">{formatNumber(rosterUsers.length, 0)} {rosterUsers.length === 1 ? "user" : "users"} with notifications on</span>}
          </div>
        )}
      </div>
      {loading ? (
        <div role="status" className="p-6 text-sm text-gray-600 dark:text-gray-300">Loading notification users...</div>
      ) : loadError ? (
        <div role="alert" className="m-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">{loadError}</div>
      ) : rosterUsers ? (
        <NotificationUsersTable users={rosterUsers} />
      ) : (
        <p className="py-24 text-center text-sm text-gray-600 dark:text-gray-300">No providers found.</p>
      )}
    </section>
  );
}
